import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { Loader2, AlertTriangle, Check, Save, FlaskConical, RotateCcw, ChevronRight } from 'lucide-react';
import { TEMPLAT_LALAI, NILAI_CONTOH, PENERANGAN_TANDA, HAD_SUBJEK, HAD_ISI, isiTandaGanti, masalahTemplat } from '../hooks/useTemplatTransaksi';

// PAYMENT EMAILS — komponen paparan sahaja. Data, templat lalai dan hook simpan
// hidup dalam src/hooks/useTemplatTransaksi.js (lihat nota kembar di sana).
//   • EmailTransaksiSenarai — ringkasan bila folder "Payment emails" ditekan
//   • EmailTransaksiEditor  — borang edit SATU templat (default export)

const sendTestFn = httpsCallable(functions, 'adminEmailSendTest');

function TitikStatus({ on }) {
    return (
        <span title={on ? 'ON' : 'OFF'} style={{
            width: 7, height: 7, borderRadius: 999, flex: 'none', display: 'inline-block',
            background: on ? 'var(--s-ok)' : 'var(--s-line-strong)',
        }} />
    );
}

// ── Skrin ringkasan (folder ditekan, belum pilih templat) ──
export function EmailTransaksiSenarai({ items, loading, ralat, onPilih }) {
    if (loading) return <div className="py-12 text-center text-slate-400"><Loader2 size={20} className="animate-spin inline" /></div>;
    const hidup = items.filter(t => t.enabled).length;
    return (
        <div className="max-w-3xl space-y-4">
            {ralat && (
                <div className="flex items-start gap-2 p-3 rounded-xl text-sm bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" /><span>{ralat}</span>
                </div>
            )}
            <p className="text-sm text-slate-600 dark:text-slate-300">
                {hidup} of {items.length} templates are ON. All templates start <strong>OFF</strong> — open one, proofread,
                press <em>Send test to me</em>, then switch it ON and save.
            </p>
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                {items.map((t, i) => (
                    <button key={t.id} type="button" onClick={() => onPilih(t.id)}
                        className={`w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60 ${i ? 'border-t border-slate-100 dark:border-slate-800' : ''}`}>
                        <TitikStatus on={t.enabled} />
                        <span className="flex-1 min-w-0">
                            <span className="block text-sm font-medium text-slate-800 dark:text-white truncate">{t.nama}</span>
                            <span className="block text-xs text-slate-500 truncate">{t.subject}</span>
                        </span>
                        <span className={`text-[11px] font-bold ${t.enabled ? 'text-emerald-600' : 'text-slate-400'}`}>{t.enabled ? 'ON' : 'OFF'}</span>
                        <ChevronRight size={16} className="text-slate-400" />
                    </button>
                ))}
            </div>
        </div>
    );
}

// ── Borang edit SATU templat ──
// Draf tempatan; hanya ditulis bila Save ditekan. Induk memberi `key={templat.id}`
// supaya bertukar templat = borang baharu (draf lama tidak terbawa ke templat lain).
export default function EmailTransaksiEditor({ templat, simpanSatu, onDirtyChange, templatLalai }) {
    const [draf, setDraf] = useState(templat);
    const [busy, setBusy] = useState('');
    const [msg, setMsg] = useState(null);

    const dirty = draf.enabled !== templat.enabled || draf.subject !== templat.subject || draf.bodyText !== templat.bodyText;
    useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
    useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

    const ubah = (patch) => { setDraf(prev => ({ ...prev, ...patch })); setMsg(null); };
    const masalah = masalahTemplat(draf);

    const resetLalai = () => {
        const asal = (templatLalai || TEMPLAT_LALAI).find(t => t.id === draf.id);
        if (!asal) return;
        if (!window.confirm('Reset this template to the default wording? Nothing is saved until you press Save.')) return;
        ubah({ subject: asal.subject, bodyText: asal.bodyText });
    };

    const handleSave = async () => {
        setBusy('save'); setMsg(null);
        try {
            await simpanSatu(draf);
            setMsg({ type: 'success', text: draf.enabled ? 'Saved. This template is ON — customers get it on their next payment.' : 'Saved. This template is OFF — no customer receives it.' });
        } catch (e) {
            setMsg({ type: 'error', text: 'Failed to save: ' + e.message });
        } finally { setBusy(''); }
    };

    const handleTest = async () => {
        if (masalah.length) { setMsg({ type: 'error', text: masalah.join(' ') }); return; }
        setBusy('test'); setMsg(null);
        try {
            const r = await sendTestFn({ subject: isiTandaGanti(draf.subject, NILAI_CONTOH), bodyText: isiTandaGanti(draf.bodyText, NILAI_CONTOH) });
            setMsg({ type: 'success', text: r.data.dryRun ? 'Practice mode: test prepared (no real email sent — Resend key not set on server).' : `Test email sent to ${r.data.to} with sample values.` });
        } catch (e) {
            setMsg({ type: 'error', text: e.message });
        } finally { setBusy(''); }
    };

    return (
        <div className="max-w-3xl space-y-4">
            {msg && (
                <div className={`flex items-start gap-2 p-3 rounded-xl text-sm ${msg.type === 'error' ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'}`}>
                    {msg.type === 'error' ? <AlertTriangle size={16} className="mt-0.5 shrink-0" /> : <Check size={16} className="mt-0.5 shrink-0" />}
                    <span>{msg.text}</span>
                </div>
            )}

            <div className={`bg-white dark:bg-slate-900 rounded-2xl border p-5 shadow-sm space-y-3 ${draf.enabled ? 'border-emerald-300 dark:border-emerald-800' : 'border-slate-200 dark:border-slate-800'}`}>
                <label className="flex items-center justify-between gap-3 cursor-pointer pb-3 border-b border-slate-100 dark:border-slate-800">
                    <span className="text-sm text-slate-600 dark:text-slate-300">Send this email to customers automatically</span>
                    <span className="flex items-center gap-2 shrink-0">
                        <input type="checkbox" checked={!!draf.enabled} onChange={e => ubah({ enabled: e.target.checked })} className="w-4 h-4 accent-emerald-600" />
                        <span className={`text-xs font-bold ${draf.enabled ? 'text-emerald-600' : 'text-slate-400'}`}>{draf.enabled ? 'ON' : 'OFF'}</span>
                    </span>
                </label>

                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Subject</label>
                    <input type="text" value={draf.subject} onChange={e => ubah({ subject: e.target.value })} placeholder="Email subject" maxLength={HAD_SUBJEK}
                        className="w-full p-3 border border-slate-200 dark:border-slate-700 rounded-xl text-sm bg-slate-50 dark:bg-slate-800 outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Message</label>
                    <textarea value={draf.bodyText} onChange={e => ubah({ bodyText: e.target.value })} rows={14} maxLength={HAD_ISI} placeholder="Message body (you can use **bold** and [links](https://...))"
                        className="w-full p-3 border border-slate-200 dark:border-slate-700 rounded-xl text-sm bg-slate-50 dark:bg-slate-800 outline-none resize-y font-mono focus:ring-2 focus:ring-emerald-500" />
                    <p className="text-[11px] text-slate-400 mt-1">Tip: <code>**bold**</code> · <code>[text](link)</code> · blank line = new paragraph.</p>
                </div>

                <div className="text-[11px] text-slate-500">
                    <span className="font-semibold">Placeholders you can use:</span>{' '}
                    {draf.tandaGanti.map(k => (
                        <span key={k} title={PENERANGAN_TANDA[k] || ''} className="inline-block mr-1 mb-1 px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 font-mono cursor-help">{`{{${k}}}`}</span>
                    ))}
                </div>

                {masalah.length > 0 && (
                    <div className="flex items-start gap-2 text-xs text-amber-600 dark:text-amber-400">
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                        <span>{masalah.join(' ')}</span>
                    </div>
                )}

                <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                    <button onClick={handleTest} disabled={!!busy}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-medium hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50">
                        {busy === 'test' ? <Loader2 size={14} className="animate-spin" /> : <FlaskConical size={14} />}
                        Send test to me
                    </button>
                    <button onClick={resetLalai} disabled={!!busy}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-slate-500 text-xs font-medium hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50">
                        <RotateCcw size={14} /> Reset to default
                    </button>
                    {dirty && <span className="text-xs text-amber-500 ml-auto">Unsaved changes</span>}
                    <button onClick={handleSave} disabled={!!busy || !dirty || masalah.length > 0}
                        className={`flex items-center gap-2 px-5 py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-sm font-bold hover:brightness-110 disabled:opacity-40 ${dirty ? '' : 'ml-auto'}`}>
                        {busy === 'save' ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
}
